import { useMutation } from '@tanstack/react-query'
import type { AdminMenuItem } from '@ytclip/shared'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { SwitchField, TextField } from '@/components/ui/field.tsx'
import { createMenuItem, updateMenuItem } from '@/features/admin/api.ts'
import { ApiRequestError } from '@/lib/api-client.ts'

interface Draft {
  label: string
  icon: string
  url: string
  position: string
  enabled: boolean
}

const BLANK: Draft = { label: '', icon: '', url: '', position: '0', enabled: true }

function toDraft(item: AdminMenuItem | null): Draft {
  if (!item) return BLANK

  return {
    label: item.label,
    icon: item.icon,
    url: item.url,
    position: String(item.position),
    enabled: item.enabled,
  }
}

export interface MenuItemDialogProps {
  item: AdminMenuItem | null
  open: boolean
  onClose: () => void
  onDone: () => void
}

export function MenuItemDialog({ item, open, onClose, onDone }: MenuItemDialogProps) {
  const [draft, setDraft] = useState<Draft>(BLANK)

  // Reset when the dialog opens, so editing one item never shows another's
  // values for a frame, and "Tambah" never reopens a previous edit.
  useEffect(() => {
    if (open) setDraft(toDraft(item))
  }, [open, item])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: draft.label.trim(),
        icon: draft.icon.trim(),
        url: draft.url.trim(),
        position: Number(draft.position) || 0,
        enabled: draft.enabled,
      }

      return item ? updateMenuItem(item.id, body) : createMenuItem(body)
    },
    onSuccess: () => {
      toast.success(item ? 'Menu diperbarui' : 'Menu ditambahkan')
      onDone()
      onClose()
    },
    onError: (error) =>
      toast.error(error instanceof ApiRequestError ? error.message : 'Gagal menyimpan menu'),
  })

  const valid =
    draft.label.trim().length > 0 &&
    draft.icon.trim().length > 0 &&
    /^https?:\/\//.test(draft.url.trim())

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
      title={item ? 'Ubah menu' : 'Tambah menu'}
      description="Muncul di sidebar aplikasi desktop saat aplikasi dibuka berikutnya."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Batal
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Simpan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Label"
          placeholder="Topup AI Credits"
          value={draft.label}
          onChange={(event) => set('label', event.target.value)}
        />
        <TextField
          label="Ikon"
          placeholder="coins"
          value={draft.icon}
          onChange={(event) => set('icon', event.target.value)}
          hint="Nama ikon lucide, misalnya coins, youtube, message-circle."
        />
        <TextField
          label="URL"
          placeholder="https://ai.ytclip.org"
          value={draft.url}
          onChange={(event) => set('url', event.target.value)}
        />
        <TextField
          label="Urutan"
          inputMode="numeric"
          value={draft.position}
          onChange={(event) => set('position', event.target.value)}
          hint="Kecil tampil lebih dulu. Angka sama diurutkan dari yang lebih lama."
        />
        <SwitchField
          label="Tampilkan di aplikasi"
          description="Nonaktifkan untuk menyembunyikan tanpa menghapus."
          checked={draft.enabled}
          onChange={(enabled) => set('enabled', enabled)}
        />
      </div>
    </Dialog>
  )
}
